import {
  closeDrawer,
  openConfirmDialog,
  openDrawer,
} from "@/src/components/dialogs";

import { Button } from "@/src/components/buttons";
import { Card, CompartmentCard } from "@/src/components/cards";
import { FetchableList } from "@/src/components/fetchableLists";
import {
  DocumentTemplate,
  Exam,
  Patient,
  PatientDocument,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, getUserShortName, uid } from "@/src/utils/utils";
import moment from "moment";
import { useState } from "react";
import { openEditor } from "./documentsHook";
import { DocumentTemplateChoiceList } from "./documentTemplates";
import { HtmlDiv } from "./htmlDiv";

export function ExamDocumentsCompartment({
  patient,
  exam,
}: {
  patient: Patient;
  exam: Exam;
}) {
  const [reload, setReload] = useState(0);

  async function choseDocumentTemplate(): Promise<DocumentTemplate | null> {
    let templates = await apiFetch("documentTemplates", "loadMany", {
      referenceId: getItemId(exam.reference),
      types: ["standard", "letter"],
    });
    let selectedTemplate: DocumentTemplate | null = null;
    if (templates.length == 1) selectedTemplate = templates[0];
    else
      await openDrawer({
        id: "documentTemplateChoiceDrawer",
        title: "Choisir un modèle de document",
        content: (
          <DocumentTemplateChoiceList
            defaultTemplates={templates}
            types={["standard", "letter"]}
            onSelect={async (template) => {
              selectedTemplate = template;
              closeDrawer("documentTemplateChoiceDrawer");
            }}
          />
        ),
      });
    return selectedTemplate;
  }

  async function createNewDocument() {
    let template = await choseDocumentTemplate();
    if (!template) return;
    let patientDocument: PatientDocument = {
      patient: patient,
      creator: USER?.get(),
      template: template,
      parent: exam,
      content: template.content,
      title: template.title || "Nouveau document",
      status: "pending",
      refAnswer: exam.content,
    };

    await openEditor({
      patient,
      patientDocument,
      onSave: async (newValue) => {
        await apiFetch(
          "patientDocuments",
          "addOne",
          {
            patientDocument: newValue,
          },
          "Erreur lors de la création du document"
        );
        setReload(reload + 1);
      },
    });
  }

  return (
    <FetchableList
      queryKey={["patientDocuments", patient.id, exam?.id, reload]}
      queryFn={async () =>
        apiFetch(
          "patientDocuments",
          "loadMany",
          {
            patientId: patient.id,
            parentId: getItemId(exam),
          },
          "Erreur lors du chargement des documents"
        )
      }
      emptyChild={
        <div className="c1 gap15 gray center">
          Aucun compte rendu généré. <br />
          <Button color="green" type={"tertiary"} onClick={createNewDocument}>
            Créer un compte rendu
          </Button>
        </div>
      }
    >
      {(data) => (
        <CompartmentCard
          title="Compte rendus"
          color="green"
          onClick={
            data?.length
              ? async () => {
                  createNewDocument();
                }
              : undefined
          }
        >
          <div className="c1 gap10">
            {data?.map((patientDocument, index) => {
              return (
                <Card
                  key={uid()}
                  color="green"
                  title={
                    <HtmlDiv>
                      {patientDocument.title ||
                        patientDocument.template?.title ||
                        ""}
                    </HtmlDiv>
                  }
                  subTitle={getUserShortName(patientDocument.creator)}
                  onClick={async () => {
                    let fullPatientDocument = await apiFetch(
                      "patientDocuments",
                      "loadOne",
                      {
                        id: patientDocument?.id,
                      },
                      "Erreur lors du chargement du document"
                    );
                    if (!fullPatientDocument)
                      throw new Error("Le Document n'existe pas.");

                    if (!fullPatientDocument.template) {
                      fullPatientDocument.template = {
                        title: fullPatientDocument.title || "",
                        content: fullPatientDocument.content,
                        format: "a4",
                        isValidatable: false,
                        creator: USER?.get(),
                        name: "",
                        showHeader: true,
                        showLeftBar: true,
                        barcodeLocation: "none",
                        showPatientIdentity: true,
                        showTitle: true,
                        parent: null,
                      };
                    }

                    openEditor({
                      patient,
                      patientDocument: fullPatientDocument,
                      onChange: () => {
                        setReload(reload + 1);
                      },
                    });
                  }}
                  onDelete={async () => {
                    await openConfirmDialog({
                      id: "deleteDocument",
                      title: "Supprimer le document",
                      content: "Voulez-vous vraiment supprimer ce document?",
                      onConfirm: async () => {
                        await apiFetch(
                          "patientDocuments",
                          "deleteOne",
                          {
                            id: patientDocument.id,
                          },
                          "Erreur lors de la suppression du document"
                        );
                        setReload(reload + 1);
                      },
                    });
                  }}
                >
                  <div className="gray">
                    {moment(patientDocument._date).format("LL HH:mm ")}
                  </div>
                </Card>
              );
            })}
          </div>
        </CompartmentCard>
      )}
    </FetchableList>
  );
}

export async function openExamDocumentsDrawer(exam: Exam) {
  if (!exam.patient) throw new Error("Patient not found");
  await openDrawer({
    id: "examDocumentsDrawer",
    title: "Compte rendu",
    content: <ExamDocumentsCompartment exam={exam} patient={exam.patient} />,
  });
  // let documentId = getItemId(exam.document);
  // let fullDocument: PatientDocument = {
  //   id: getItemId(exam.document),
  //   content: exam.conclusion || "",
  //   patient: exam.patient,
  //   status: "pending",
  //   refAnswer: {
  //     patient: getItemId(exam.patient),
  //     fieldAnswers: exam.content.fieldAnswers,
  //     reference: exam.reference,
  //     creator: getUserId(),
  //   },
  //   creator: getUserId(),
  // };

  // if (fullDocument.id)
  //   fullDocument = await apiFetch("patientDocuments","loadPatientDocument",{
  //     id: documentId,
  //   });

  // await openEditor({
  //   patient: exam.patient,
  //   patientDocument: fullDocument,
  //   onSave: async (newValue) => {
  //     await apiFetch("patientDocuments", "addPatientDocument", { patientDocument: newValue });
  //   },
  // });
}
